/**
 * Types for the Redshift → S3 export pipeline (the `redshiftExports`
 * DynamoDB table). A run points the vendored `export_to_s3` Python
 * pipeline (packages/worker/redshift-export) at one Redshift schema and has
 * Redshift `UNLOAD` write every table straight to S3 as CSV. Nothing is
 * streamed through the worker, so run time is bounded by Redshift and S3
 * rather than by the task's network.
 *
 * Distinct from `export.ts`, which exports one agency's rows out of the
 * production Postgres and hands them to a client over SFTP. This pipeline is
 * an internal bulk dump: whole schema, no tenant scoping, output stays in S3.
 *
 * Mirrors the shape/philosophy of `Job` in job.ts: timestamps are ISO-8601
 * strings. Redshift
 * credentials never touch the document — the worker reads them from Secrets
 * Manager.
 */
/** Slug of the app that owns this pipeline, used for per-app authorization. */
export const REDSHIFT_EXPORT_APP_SLUG = 'redshift-export';
export const REDSHIFT_TARGET_LABELS = {
    new: 'New Redshift',
    legacy: 'Old Redshift',
};
/** The cluster a run reads: `legacy` for a run that predates the choice. */
export function redshiftExportTarget(job) {
    return job.redshiftTarget ?? 'legacy';
}
/**
 * Redshift identifiers are embedded in the UNLOAD statement, and the Python
 * pipeline rejects anything outside this character set before building SQL.
 * Enforced here too so a bad schema name fails in the form rather than in the
 * worker twenty seconds later.
 */
export const REDSHIFT_IDENT_PATTERN = /^[A-Za-z0-9_]+$/;
/** Why a schema name is unacceptable, or null when it is fine. */
export function redshiftSchemaError(schema) {
    const value = schema.trim();
    if (!value)
        return 'Enter a schema name.';
    if (value.length > 127)
        return 'Schema names are at most 127 characters.';
    if (!REDSHIFT_IDENT_PATTERN.test(value)) {
        return 'Use only letters, numbers and underscores.';
    }
    return null;
}
/**
 * Migration scratch and mapping tables, pre-filled into the form's exclusions.
 * These are a default rather than a floor: they live here instead of in the
 * pipeline's config.yml so an operator can see exactly what is being skipped and
 * remove an entry when a run genuinely needs one of these tables.
 */
export const DEFAULT_REDSHIFT_EXCLUDE_TABLES = [
    'atlas_users*',
    'user_mapping*',
    'people_dupe*',
    'people_to_import*',
    'companies_to_import*',
    'meeting_attribute*',
    'meeting_actions*',
];
/**
 * Standard stems that stay out of the form's picker. They are migration
 * file-mapping artefacts rather than client data, so offering them invites a
 * pointless export.
 *
 * They must nonetheless remain in the pipeline's `standard_file_names`: that
 * list is how a table is mapped onto its output stem, and dropping an entry
 * would silently rename output. `file_person_mapping` would fall through to the
 * suffix-stripping branch of `table_to_filename` and land in S3 as
 * `file_person.csv`.
 */
export const REDSHIFT_STEMS_NOT_OFFERED = [
    'file_person_mapping',
    'company_map_files',
];
/**
 * Canonical, CRM-independent table names, offered as the include-filter options
 * in the form. Kept in the same order as `standard_file_names` in the vendored
 * pipeline's scripts/config.yml, minus REDSHIFT_STEMS_NOT_OFFERED, so the two
 * can be diffed by eye — and asserted to be exactly that by the tests.
 *
 * That list is what the pipeline uses to map a CRM-suffixed table
 * (`people_vincere`) onto its output stem (`people`), and includes are matched
 * against the stem as well as the raw name — so choosing `people` here selects
 * whichever suffixed variant the schema actually holds, with no need to know
 * the CRM.
 */
export const REDSHIFT_STANDARD_TABLES = [
    'users',
    'people',
    'person_identities',
    'companies',
    'company_identities',
    'company_contacts',
    'educations',
    'experiences',
    'person_notes',
    'company_notes',
    'projects',
    'project_company_contacts',
    'candidates',
    'fee_types',
    'placements',
    'project_fees',
    'project_fee_splits',
    'meetings',
    'custom_attributes',
    'custom_attribute_options',
    'person_custom_attribute_values',
    'person_custom_values',
    'project_custom_attribute_values',
    'project_custom_values',
    'company_custom_attribute_values',
    'company_custom_values',
    'meeting_custom_attribute_values',
    'person_salaries',
    'person_relationships',
    'person_references',
    'person_contractors',
    'project_users',
    'company_locations',
    'company_relationships',
    'opportunities',
    'opportunity_people',
    'opportunity_target_companies',
    'tasks',
    'todo_tags',
    'task_todo_tags',
    'lists',
    'list_memberships',
];
/**
 * Table filters are typed as a comma-separated list in the UI and passed to the
 * pipeline the same way, so both sides agree on the parse. Glob characters are
 * deliberately preserved — `audit_*` is a legitimate pattern.
 */
export function parseTablePatterns(raw) {
    const seen = new Set();
    const out = [];
    for (const part of raw.split(',')) {
        const pattern = part.trim();
        if (!pattern || seen.has(pattern))
            continue;
        seen.add(pattern);
        out.push(pattern);
    }
    return out;
}
/**
 * A pattern is only safe to interpolate into the pipeline's env if it is a
 * table-name glob. Rejecting commas matters because the pipeline splits its
 * include/exclude env vars on them.
 */
export const TABLE_PATTERN_RE = /^[A-Za-z0-9_?*[\]-]+$/;
export function tablePatternError(pattern) {
    if (!TABLE_PATTERN_RE.test(pattern)) {
        return `"${pattern}" is not a valid table pattern — use letters, numbers, underscores and * ? [ ].`;
    }
    return null;
}
/**
 * Whether a table name matches one filter pattern, following the same rules as
 * the pipeline's `fnmatch`-based matching: `*`, `?` and `[…]` globs, compared
 * case-insensitively. Used by the form to warn when a chosen table would be
 * dropped by an exclusion anyway — exclusions win over includes, so without the
 * warning the run would quietly skip it.
 */
export function tablePatternMatches(pattern, name) {
    let regex = '';
    let i = 0;
    while (i < pattern.length) {
        const char = pattern[i];
        if (char === '*') {
            regex += '.*';
            i += 1;
        }
        else if (char === '?') {
            regex += '.';
            i += 1;
        }
        else if (char === '[') {
            const close = pattern.indexOf(']', i + 1);
            // An unterminated bracket is a literal one, as in fnmatch.
            if (close < 0) {
                regex += '\\[';
                i += 1;
            }
            else {
                const body = pattern.slice(i + 1, close);
                regex += `[${body.startsWith('!') ? `^${body.slice(1)}` : body}]`;
                i = close + 1;
            }
        }
        else {
            regex += char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            i += 1;
        }
    }
    return new RegExp(`^${regex}$`, 'i').test(name);
}
/**
 * Default S3 key prefix for a run's output, e.g. `vincere/acme_migrated/`. The
 * source system leads so a bucket groups by CRM, matching the source folder
 * slugs the import side already writes under.
 */
export function buildRedshiftExportPrefix(schema, sourceSystem = '') {
    const source = sourceSystem.trim().toLowerCase();
    return `${source ? `${source}/` : ''}${schema.trim().toLowerCase()}/`;
}
/** Normalise a caller-supplied prefix to no leading and exactly one trailing slash. */
export function normalizeS3Prefix(prefix) {
    const trimmed = prefix.trim().replace(/^\/+|\/+$/g, '');
    return trimmed ? `${trimmed}/` : '';
}
/**
 * Buckets a Redshift export may write to. The caller picks one of these and the
 * path beneath it; anything else is rejected, because the destination has to be
 * matched by an IAM grant to work at all.
 *
 * `prod-atlas-uploads-465358817960` is the production uploads bucket; imports
 * are pinned under `000-import/` so a run cannot touch the rest of it.
 *
 * The Redshift export grants in infra/lib/compute.nested-stack.ts are built
 * from this list.
 */
export const REDSHIFT_EXPORT_DESTINATIONS = [
    { bucket: 'atlas-import-data', basePrefix: '' },
    { bucket: 'prod-atlas-uploads-465358817960', basePrefix: '000-import/' },
];
/** Destination used when the caller does not name one. */
export const DEFAULT_REDSHIFT_EXPORT_BUCKET = 'atlas-import-data';
/** The allowed destination for a bucket, or null when it is not one of them. */
export function redshiftExportDestination(bucket) {
    const wanted = bucket.trim().toLowerCase();
    return REDSHIFT_EXPORT_DESTINATIONS.find((d) => d.bucket === wanted) ?? null;
}
/**
 * Full key prefix for a run: the bucket's mandatory base, then the path the
 * caller chose, falling back to `<source>/<schema>/`. A path that already
 * carries the base is not double-prefixed, so what an operator sees in the form
 * and what they can paste back in agree.
 *
 * Returns null for a bucket that is not an allowed destination.
 */
export function resolveRedshiftExportPrefix(opts) {
    const destination = redshiftExportDestination(opts.bucket);
    if (!destination)
        return null;
    const { basePrefix } = destination;
    const tail = normalizeS3Prefix(opts.path ?? '') ||
        buildRedshiftExportPrefix(opts.schema, opts.sourceSystem);
    const relative = basePrefix && tail.startsWith(basePrefix) ? tail.slice(basePrefix.length) : tail;
    return `${basePrefix}${relative}`;
}
/**
 * Folder files-mapping exports share with Move attachments documents, so the
 * CSVs land beside the flattened files rather than one level above them.
 */
export const REDSHIFT_MAPPING_FOLDER = 'all_files';
/**
 * Destination for Files mapping mode: the normal export prefix, then
 * `all_files/`. e.g. `000-import/jobadder/theculletonpartnersh/all_files/`.
 * Paths that already end in that folder are left alone.
 */
export function resolveRedshiftMappingPrefix(opts) {
    const prefix = resolveRedshiftExportPrefix(opts);
    if (!prefix)
        return null;
    const folder = `${REDSHIFT_MAPPING_FOLDER}/`;
    if (prefix === folder || prefix.endsWith(`/${folder}`))
        return prefix;
    return `${prefix}${folder}`;
}
//# sourceMappingURL=redshiftExport.js.map